import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { DisclosureGroup } from "react-aria-components";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import Form from "@/components/Form/Form";
import type { IActivityCategory } from "@medoma/models/activities";
import { calendarDateSchema } from "@medoma/models/date-and-time";
import type { IPatientId } from "@medoma/models/patients";
import { convertDurationToMinutes } from "@/forms/AddActivityForm/activityDurationUtils";
import { ActivityPanel } from "./ActivityPanel";
import { NewActivityPanel } from "./NewActivityPanel";
import { newActivityRow } from "./activityRow";
import type { IRegisterActivitiesRetroactivelyFormData } from "./RegisterActivityRetroactivelyForm";
import { useSubmitRetroactively } from "./useSubmitRetroactively";
import styles from "./RegisterActivityRetroactivelyForm.module.scss";

export const GroupedRetroForm = ({
  patientId,
  category,
}: {
  patientId: IPatientId;
  category: IActivityCategory;
}) => {
  const { t } = useLingui();
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useFormContext<IRegisterActivitiesRetroactivelyFormData>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "activities",
  });
  const [isAddingActivity, setIsAddingActivity] = useState(true);

  const { mutate, isPending } = useSubmitRetroactively({ patientId });

  const unsavedIndex = isAddingActivity ? fields.length - 1 : null;
  const savedFields = isAddingActivity ? fields.slice(0, -1) : fields;
  const unsavedField = unsavedIndex !== null ? fields[unsavedIndex] : undefined;
  const canRemoveSavedActivity = savedFields.length > 1;

  const startAddingActivity = () => {
    append(newActivityRow());
    setIsAddingActivity(true);
  };

  const stopAddingActivity = () => {
    clearErrors("root.activities");
    setIsAddingActivity(false);
  };

  const cancelAddingActivity = () => {
    if (unsavedIndex !== null) {
      remove(unsavedIndex);
    }
    stopAddingActivity();
  };

  const validateAndSubmit = handleSubmit((formData) => {
    if (isAddingActivity) {
      setError("root.activities", {
        type: "unsaved",
        message: t`Du har en aktivitet som inte är sparad. Klicka på "Klar" innan du efterregistrerar.`,
      });
      return;
    }

    if (formData.activities.length === 0) {
      setError("root.activities", {
        type: "required",
        message: t`Lägg till minst en aktivitet i besöket`,
      });
      return;
    }

    mutate({
      submission: {
        kind: "group",
        group: {
          careEpisodeId: patientId,
          shiftId: formData.shiftId,
          date: calendarDateSchema.decode(formData.startDate),
          time: formData.time,
          category,
          activities: formData.activities.map((activity) => ({
            activityId: activity.activityId,
            title: activity.title.trim(),
            description: activity.description?.trim() || undefined,
            duration: convertDurationToMinutes(
              activity.durationOption,
              activity.customDuration,
              activity.customDurationUnit,
            ),
          })),
        },
      },
      activities: formData.activities,
    });
  });

  if (isPending) {
    return <Loading message={t`Efterregistrerar`} />;
  }

  return (
    <Form onSubmit={validateAndSubmit}>
      <section className={styles.activityListSection}>
        <Heading level="h3" weight="medium">
          <Trans>Aktiviteter i besöket</Trans>
        </Heading>
        {errors.root?.activities?.message ? (
          <ErrorMessage message={errors.root.activities.message} />
        ) : null}
        {savedFields.length > 0 ? (
          <DisclosureGroup className={styles.savedActivitiesGroup}>
            {savedFields.map((field, index) => (
              <ActivityPanel
                key={field.id}
                id={field.id}
                index={index}
                patientId={patientId}
                category={category}
                onRemove={
                  canRemoveSavedActivity ? () => remove(index) : undefined
                }
              />
            ))}
          </DisclosureGroup>
        ) : null}
        {unsavedIndex !== null && unsavedField ? (
          <NewActivityPanel
            key={unsavedField.id}
            index={unsavedIndex}
            patientId={patientId}
            category={category}
            onSave={stopAddingActivity}
            onCancel={cancelAddingActivity}
          />
        ) : (
          <PlainButton
            contentType="text-and-icon"
            width="fill-container"
            onClick={startAddingActivity}
          >
            <PlusIcon />
            <Trans>Lägg till aktivitet i besöket</Trans>
          </PlainButton>
        )}
      </section>
      <FilledButton type="submit">
        <Plural
          value={fields.length}
          one="Efterregistrera aktiviteten"
          other="Efterregistrera aktiviteterna"
        />
      </FilledButton>
    </Form>
  );
};
